import { getRailsClient } from "../client";
import {
  Assessment,
  Question,
  Quiz,
  QuizSubmissionRequest,
  QuestionSubmissionResponse,
  UserStatistics,
} from "./types/quizTypes";

interface QuizResponse {
  assessment: Assessment;
  userStatistics: UserStatistics;
  question: Question;
  nextLesson: {
    id: number;
    levelId: number | null;
    lessonKey: string;
  } | null;
  noMoreAttempts: boolean;
}

// Verified from PUT /api/internal_api/atlas/v1/quiz/:lessonKey/submit
interface AtlasQuizSubmissionResponse {
  assessmentData: {
    id: number;
    answersCount: number;
    assessmentScore: number;
    displayScore: string;
    questionsCount: number;
    result: string;
    rightAnswersCount: number;
    showAfterSurvey: boolean;
    nextLesson: { id: number; levelId: number } | null;
    questionsData: QuestionSubmissionResponse[];
  };
}

export const getAssessment = async (lessonId: number): Promise<QuizResponse> => {
  try {
    const client = getRailsClient();
    // TODO: migrate to atlas endpoint; Rails accepts 'undefined' here as a legacy no-op segment
    const API = `/api/internal_api/v1/levels/undefined/lessons/${lessonId}/assessments`;
    const { data } = await client.get<QuizResponse>(API);
    return data;
  } catch (error) {
    console.error("Error getting assessment:", error);
    throw error;
  }
};

export const submitQuizQuestion = async ({
  slug,
  questionId,
}: {
  slug: string;
  questionId: string;
}) => {
  try {
    const client = getRailsClient();
    const API = `/api/internal_api/v1/assessment_questions/${questionId}`;
    const { data } = await client.put<Assessment>(API, {
      assessmentQuestion: {
        slug: slug,
      },
    });
    return data;
  } catch (error) {
    console.error("Error submitting quiz question:", error);
    throw error;
  }
};

export const retakeQuiz = async (lessonId: number) => {
  try {
    const client = getRailsClient();
    // TODO: migrate to atlas endpoint; Rails accepts 'undefined' here as a legacy no-op segment
    const API = `/api/internal_api/v1/levels/undefined/lessons/${lessonId}/assessments`;
    const { data } = await client.post<Assessment>(API);
    return data;
  } catch (error) {
    console.error("Error restarting quiz:", error);
    throw error;
  }
};

export const getQuiz = async (lessonKey: string) => {
  try {
    const client = getRailsClient();
    const API = `/api/internal_api/atlas/v1/quiz/${lessonKey}`;
    const { data } = await client.get<Quiz>(API);
    return data;
  } catch (error) {
    console.error("Error loading quiz:", error);
    throw error;
  }
};

export const submitQuiz = async (lessonKey: string, quiz: QuizSubmissionRequest) => {
  try {
    const client = getRailsClient();
    const API = `/api/internal_api/atlas/v1/quiz/${lessonKey}/submit`;
    const { data } = await client.put<AtlasQuizSubmissionResponse>(API, {
      assessment: quiz,
    });
    return data;
  } catch (error) {
    console.error(`Error submitting quiz for lessonKey ${lessonKey}:`, error);
    throw error;
  }
};

export const retryQuiz = async (lessonKey: string) => {
  try {
    const client = getRailsClient();
    const API = `/api/internal_api/atlas/v1/quiz/${lessonKey}`;
    const { data } = await client.post<Quiz>(API);
    return data;
  } catch (error) {
    console.error(`Error retrying quiz for lessonKey ${lessonKey}:`, error);
    throw error;
  }
};
